// JobExecutionPage.tsx — with resume-from-checkpoint
//
// FIX: getTables() now deduplicates FieldGroups by origName so DQ Rule
// Execution only asks for ONE file upload per unique source CSV, not one
// per logical FieldGroup.
//
// LAZY-LOAD FIX: when the DM tree hasn't shipped the entity's field groups
// (entity.field is empty under lazy loading), the source files for DQ Rule
// Execution are fetched on demand via getEntitySourceFiles().

import { parseServerTime } from "./serverTime";
import React, { useState, useEffect, useRef, useCallback, ChangeEvent } from "react";
import DQExecutionPanel from "./DQExecutionPanel";
import KnowledgeSourcesPanel from "../ekd/KnowledgeSourcesPanel";
import {
  adriva_executeDQWithUpload,
  adriva_getExecutionPlan,
  adriva_executeOnConnection,
  adriva_listConnections,
  adriva_runPipeline,
  adriva_runBatch,
  adriva_getBatchStatus,
  getEntitySourceFiles,
  adriva_getActivePipeline,
  adriva_cancelPipeline,
} from "../API";

// retired jobs: kept in the backend, no longer offered (BC agents no longer used; rules are discovered by DQ_FIND_FIELD_DEPENDENCIES)
const RETIRED_JOBS = new Set<string>(["BUSINESS_CONTROL_DISCOVERY", "DQ_GENERATE_RULES", "DQ_AUTO_APPROVE"]);
const DG_JOBS = ["DG_BUSINESS_METADATA_ENRICH", "DG_PRIVACY_REGULATION_ENRICH", "DG_CDE_ENRICH"];
const MDM_JOBS = ["BUILD_CANONICAL_DM", "LOOKUP_DATA_STANDARDIZATION", "LOOKUP_DATA_FIND_ANOMALIES", "DE_DUPLICATION_RULE_GENERATE"];
const DQ_JOBS = ["LOOKUP_DETECTION", "ENTERPRISE_KNOWLEDGE_DISCOVERY", "DQ_FIND_FIELD_DEPENDENCIES", "DQ_RULE_EXECUTION"];
type Stage = [string[], string, string?];
/** the Run page follows the application's MODULE; the other modules' jobs are offered collapsed ("Add ...") */
function stagesFor(tool?: string): { main: Stage[]; more: Stage[] } {
  if (tool === "BUILD_DG_USING_DATA") return {
    main: [[["PROFILE", "BUILD_DM"], "① Prepare — profile and data model, run automatically from the schema"],
           [["DG_BUSINESS_METADATA_ENRICH"], "② Enrich metadata — business terms, definitions, taxonomy (Steward gate)"],
           [["DG_PRIVACY_REGULATION_ENRICH"], "③ Classify — privacy, sensitivity and regulation (Legal & Privacy gate)"],
           [["DG_CDE_ENRICH"], "④ Critical data elements"]],
    more: [[DQ_JOBS, "Add data quality", "lookup columns · Enterprise Knowledge · DQ rules · execution"],
           [MDM_JOBS, "Master data management", "canonical model · lookup standardization & anomalies · de-duplication"]] };
  if (tool === "BUILD_MDM_USING_DATA") return {
    main: [[["PROFILE", "LOOKUP_DETECTION", "BUILD_DM"], "① Prepare — profile, lookup columns (SME-certified), data model"],
           [["ENTERPRISE_KNOWLEDGE_DISCOVERY", "DQ_FIND_FIELD_DEPENDENCIES"], "② Data quality — Enterprise Knowledge, DQ rules (cleansing rules: coming)"],
           [MDM_JOBS, "③ Master data — canonical model, lookup harmonization, de-duplication"]],
    more: [[["DQ_RULE_EXECUTION"], "Execute DQ rules", "run the approved rules on source data"],
           [DG_JOBS, "Add governance insights", "business glossary & metadata · privacy & classification · critical data elements"]] };
  return {                                                     // DQ (and unknown -> DQ, today's view)
    main: [[["PROFILE", "BUILD_DM"], "① Prepare — runs automatically when the data is uploaded"],
           [["LOOKUP_DETECTION", "ENTERPRISE_KNOWLEDGE_DISCOVERY"], "② Understand — lookup columns (SME-certified), then Enterprise Knowledge"],
           [["DQ_FIND_FIELD_DEPENDENCIES"], "③ Discover — DQ rules"],
           [["DQ_RULE_EXECUTION"], "④ Execute & monitor"]],
    more: [[DG_JOBS, "⑤ Add governance insights", "business glossary & metadata · privacy & classification · critical data elements"],
           [MDM_JOBS, "Master data management", "canonical model · lookup standardization & anomalies · de-duplication"]] };
}
const pickJobs = (types: string[]) => types.map((t) => JOB_CATALOG.find((j) => j.job_type === t)).filter(Boolean) as any[];

const JOB_CATALOG = [
  { job_type: "PROFILE",                       label: "Profile the data",                 icon: "📊", desc: "Profile every table: types, formats, value lists, blanks.",                                                   requiresFiles: false, group: "discovery" },
  { job_type: "LOOKUP_DETECTION",              label: "Identify lookup columns",                icon: "🔍", desc: "Predicts lookup / reference columns from the profile - the SME certifies them.",                          requiresFiles: false, group: "discovery" },
  { job_type: "BUILD_DM",                      label: "Build the data model",                icon: "🗂", desc: "Tables, keys and relationships of the entity.",              requiresFiles: false, group: "discovery" },
  
  
  { job_type: "ENTERPRISE_KNOWLEDGE_DISCOVERY",   label: "Build Enterprise Knowledge",      icon: "⚙️", desc: "What every field means, its codes, its owner - from the data and your documents.",                   requiresFiles: false, group: "dq"        },
  { job_type: "BUSINESS_CONTROL_DISCOVERY",   label: "BUSINESS_CONTROL_DISCOVERY",      icon: "⚙️", desc: "AI-generate field dependencies for each attribute.",                   requiresFiles: false, group: "dq"        },

  //{ job_type: "BUSINESS_CONTROL_FROM_FIELD_DEPENDECY",   label: "BUSINESS_CONTROL_FROM_FIELD_DEPENDECY",      icon: "⚙️", desc: "AI-generate field dependencies for each attribute.",                   requiresFiles: false, group: "dq"        },


  //{ job_type: "BUSINESS_CONTROL_SCHEMA_RESOLUTION", label: "BUSINESS_CONTROL_SCHEMA_RESOLUTION",               icon: "⚙️", desc: "AI-generate Data Quality rules for each attribute.",  requiresFiles: false, group: "dq"        },
  //{ job_type: "DQ_RULE_DERIVATION", label: "DQ_RULE_DERIVATION",               icon: "⚙️", desc: "AI-generate Data Quality rules for each attribute.",                   requiresFiles: false, group: "dq"        },
  //{ job_type: "DQ_RULE_EXECUTION_COMPILOR", label: "DQ_RULE_EXECUTION_COMPILOR",               icon: "⚙️", desc: "AI-generate Data Quality rules for each attribute.",                   requiresFiles: false, group: "dq"        },


  { job_type: "DQ_FIND_FIELD_DEPENDENCIES",   label: "Discover DQ rules",      icon: "⚙️", desc: "AI agents discover, challenge and dry-run DQ rules for every field.",                   requiresFiles: false, group: "dq"        },
  { job_type: "DQ_GENERATE_RULES",             label: "Generate DQ Rules",               icon: "⚙️", desc: "AI-generate Data Quality rules for each attribute.",                   requiresFiles: false, group: "dq"        },
  //{ job_type: "DQ_RULE_MERIT",                 label: "Rule Merit Validation",           icon: "✅", desc: "Score and validate generated rules.",                                    requiresFiles: false, group: "dq"        },
  { job_type: "DQ_AUTO_APPROVE",               label: "Auto-Approve Rules",              icon: "🤖", desc: "Automatically approve rules exceeding the merit threshold.",            requiresFiles: false, group: "dq"        },
  { job_type: "DQ_RULE_EXECUTION",             label: "DQ Rule Execution",               icon: "▶️", desc: "Execute approved DQ rules against live source data.",                 requiresFiles: true,  group: "dq"        },
  { job_type: "DG_BUSINESS_METADATA_ENRICH",   label: "Metadata Enrichment",             icon: "⚙️", desc: "Automatically populate the business metadata.",                        requiresFiles: false, group: "dg"        },
  { job_type: "DG_PRIVACY_REGULATION_ENRICH",  label: "Metadata Classification-Privacy", icon: "⚙️", desc: "Automatically populate the classification/privacy.",                  requiresFiles: false, group: "dg"        },
  { job_type: "DG_CDE_ENRICH",                 label: "Critical Data Element",           icon: "⚙️", desc: "Automatically populate the CDE.",                                      requiresFiles: false, group: "dg"        },
  { job_type: "BUILD_CANONICAL_DM",            label: "Build Canonical Data Model",      icon: "🗂", desc: "Generate a structured data model from source entity attributes.",       requiresFiles: false, group: "mdm" },
  { job_type: "LOOKUP_DATA_STANDARDIZATION",   label: "Lookup Data Standardization",     icon: "⚙️", desc: "Standardize the lookup data code-desc.",                               requiresFiles: false, group: "mdm"       },
  { job_type: "LOOKUP_DATA_FIND_ANOMALIES",    label: "Lookup Data Anomalies",           icon: "⚙️", desc: "Find anomalies in current lookup code-desc.",                          requiresFiles: false, group: "mdm"       },
  { job_type: "DE_DUPLICATION_RULE_GENERATE",  label: "De-duplication Rule",             icon: "🗂", desc: "Generate De-duplication rule.",       requiresFiles: false, group: "mdm" },
] as const;

type JobType  = typeof JOB_CATALOG[number]["job_type"];
type RunMode  = "fresh" | "resume";

interface IncompleteRun {
  process_id:    string;
  progress:      number;
  message:       string | null;
  start_time:    string | null;
  status:        "running" | "failed" | "stuck";
  done_types:    string[];
  pending_types: string[];
}

interface BatchEntityProgress {
  entity_id:    string;
  entity_label: string;
  status:       "queued" | "running" | "done" | "failed";
  process_id?:  string;
  started_at?:  string;
  finished_at?: string;
  error?:       string;
}

interface Props {
  selectedEntity:   any;
  selectedEntities: any[];
  appNm:            string;
  isBatchMode:      boolean;
  entityJobs?:      any[] | null;
  tool?:            string;          // the application module: stages follow DQ / DG / MDM
}

const CSS = `
  .jep * { box-sizing: border-box; margin: 0; padding: 0; }
  .jep { font-family: 'Inter', sans-serif; height: 100%; background: #f3f4f6; overflow-y: auto; padding: 28px 36px; color: #111827; }
  .jep-section { font-size: 10.5px; font-weight: 600; color: #9ca3af; letter-spacing: 0.6px; text-transform: uppercase; margin-bottom: 10px; }
  .jep-full { display: flex; align-items: flex-start; gap: 10px; padding: 12px 16px; border-radius: 9px; cursor: pointer; border: 1.5px solid #e5e7eb; background: #fff; transition: all 0.15s; margin-bottom: 20px; font-size: 13.5px; color: #374151; user-select: none; }
  .jep-full:hover { border-color: #a5b4fc; }
  .jep-full.on    { border-color: #6366f1; background: #eef2ff; color: #3730a3; }
  .jep-full input { width: 15px; height: 15px; accent-color: #4f46e5; flex-shrink: 0; margin-top: 2px; }
  .jep-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 10px; margin-bottom: 18px; }
  .jep-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 14px 16px; cursor: pointer; position: relative; transition: border-color 0.14s, box-shadow 0.14s, background 0.14s; display: flex; flex-direction: column; gap: 5px; }
  .jep-card:hover         { border-color: #a5b4fc; box-shadow: 0 2px 8px rgba(99,102,241,0.08); }
  .jep-card.sel           { border-color: #6366f1; background: #eef2ff; box-shadow: 0 0 0 3px rgba(99,102,241,0.12); }
  .jep-card.done-card     { border-color: #86efac; background: #f0fdf4; cursor: default; opacity: .75; }
  .jep-card.pending-card  { border-color: #fde68a; background: #fffbeb; }
  .jep-check { position: absolute; top: 10px; right: 10px; width: 20px; height: 20px; border-radius: 50%; background: #4f46e5; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 11px; font-weight: 700; }
  .jep-done-badge    { position: absolute; top: 10px; right: 10px; font-size: 10px; font-weight: 700; background: #dcfce7; color: #15803d; padding: 2px 8px; border-radius: 99px; border: 1px solid #86efac; }
  .jep-pending-badge { position: absolute; top: 10px; right: 10px; font-size: 10px; font-weight: 700; background: #fef9c3; color: #92400e; padding: 2px 8px; border-radius: 99px; border: 1px solid #fde68a; }
  .jep-card-head  { display: flex; align-items: center; gap: 8px; }
  .jep-card-label { font-size: 13.5px; font-weight: 600; color: #111827; }
  .jep-card-desc  { font-size: 12px; color: #6b7280; line-height: 1.5; }
  .jep-file-badge { display: inline-flex; align-items: center; gap: 3px; font-size: 10.5px; padding: 2px 7px; border-radius: 4px; background: #fef3c7; color: #92400e; border: 1px solid #fde68a; font-weight: 500; margin-top: 2px; align-self: flex-start; }
  .jep-group-title { font-size: 10px; font-weight: 700; color: #9ca3af; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 8px; margin-top: 4px; }
  .jep-pipeline { display: flex; align-items: center; flex-wrap: wrap; gap: 5px; padding: 10px 14px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 20px; }
  .jep-pip-step         { display: flex; align-items: center; gap: 4px; font-size: 12px; font-weight: 500; color: #374151; background: #fff; border: 1px solid #e5e7eb; padding: 3px 10px; border-radius: 99px; }
  .jep-more { margin: 10px 0 4px; border: 1px dashed #d9c2f5; border-radius: 10px; padding: 8px 12px; background: #fcfaff; }
  .jep-more > summary { cursor: pointer; font-weight: 700; font-size: 12.5px; color: #460073; list-style: none; }
  .jep-more > summary span { font-weight: 500; color: #6b7280; margin-left: 8px; font-size: 12px; }
  .jep-more[open] > summary { margin-bottom: 6px; }
  .jep-plan { margin: 6px 0 10px; padding: 10px 12px; background: #faf8fd; border: 1px solid #e6dcf5; border-radius: 8px; font-size: 12.5px; color: #33424e; }
  .jep-plan-sel { display: flex; gap: 14px; align-items: center; margin-bottom: 6px; }
  .jep-plan-sel label { display: flex; gap: 5px; align-items: center; cursor: pointer; }
  .jep-plan-need ul { margin: 4px 0 0; padding-left: 18px; } .jep-plan-need li { margin: 2px 0; }
  .jep-plan-db { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; margin: 2px 0 8px 0; }
  .jep-plan-db label { display: flex; gap: 6px; align-items: center; }
  .jep-plan-db select, .jep-plan-db input { border: 1px solid #d1d5db; border-radius: 6px; padding: 4px 8px; font: inherit; }
  .jep-plan-hint { color: #6b7280; font-size: 11.5px; }
  .jep-plan-warn { color: #92400e; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; padding: 6px 8px; }
  .jep-pip-step.files   { border-color: #fde68a; background: #fffbeb; color: #92400e; }
  .jep-pip-step.skipped { border-color: #86efac; background: #f0fdf4; color: #15803d; text-decoration: line-through; opacity: .7; }
  .jep-pip-step.resume  { border-color: #0d9488; background: #f0fdfa; color: #0f766e; font-weight: 700; }
  .jep-pip-arrow { color: #d1d5db; font-size: 11px; }
  .jep-divider { height: 1px; background: #e5e7eb; margin: 20px 0; }
  .jep-file-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; gap: 12px; margin-bottom: 8px; }
  .jep-file-row.ready { border-color: #6ee7b7; background: #f0fdf4; }
  .jep-file-name      { font-weight: 700; font-size: 14px; color: #111827; font-family: 'JetBrains Mono', monospace; }
  .jep-file-groups    { font-size: 11px; color: #6b7280; margin-top: 3px; }
  .jep-file-ok        { font-size: 11.5px; color: #059669; margin-top: 3px; }
  .jep-file-input { padding: 5px 8px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 12.5px; background: #fff; color: #374151; outline: none; cursor: pointer; flex-shrink: 0; }
  .jep-step { animation: jepin .22s ease-out; }
  .jep-step.fwd { --jep-from: 28px; } .jep-step.back { --jep-from: -28px; }
  @keyframes jepin { from { opacity: 0; transform: translateX(var(--jep-from, 0)); } to { opacity: 1; transform: none; } }
  .jep-step-head { display: flex; align-items: center; gap: 14px; margin: 4px 0 14px; }
  .jep-step-title { font-size: 15px; font-weight: 700; color: #1d2a35; }
  .jep-step-sub { font-size: 12.5px; color: #6b7280; }
  .jep-back { all: unset; cursor: pointer; font-size: 13px; font-weight: 600; color: #4f46e5; border: 1px solid #c7d2fe; border-radius: 8px; padding: 8px 14px; background: #fff; }
  .jep-back:hover { background: #eef2ff; }
  .jep-input-card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px 14px; margin-bottom: 14px; background: #fff; }
  .jep-opt { margin-left: 8px; font-weight: 500; text-transform: none; letter-spacing: 0; font-size: 10.5px; color: #059669; background: #ecfdf5; border-radius: 8px; padding: 1px 7px; }
  .jep-run { all: unset; box-sizing: border-box; display: flex; align-items: center; justify-content: center; gap: 9px; padding: 12px 28px; background: #4f46e5; color: #fff; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; transition: background 0.15s, transform 0.12s; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
  .jep-run:hover:not(:disabled) { background: #4338ca; transform: translateY(-1px); }
  .jep-run.teal  { background: #0f766e; }
  .jep-run.teal:hover:not(:disabled) { background: #0d9488; }
  .jep-run:disabled { background: #e5e7eb; color: #9ca3af; cursor: not-allowed; transform: none; box-shadow: none; }
  .jep-ring    { width: 16px; height: 16px; border-radius: 50%; flex-shrink: 0; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; animation: jep-spin 0.8s linear infinite; }
  .jep-ring-sm { width: 13px; height: 13px; border-radius: 50%; flex-shrink: 0; display: inline-block; border: 2px solid rgba(99,102,241,0.2); border-top-color: #6366f1; animation: jep-spin 0.8s linear infinite; }
  @keyframes jep-spin { to { transform: rotate(360deg); } }
  .jep-alert { padding: 10px 14px; border-radius: 8px; font-size: 13.5px; margin-bottom: 14px; }
  .jep-alert.ok  { background: #f0fdf4; color: #065f46; border: 1px solid #bbf7d0; }
  .jep-alert.err { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
  .jep-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; gap: 12px; color: #9ca3af; text-align: center; padding: 60px; }
  .jep-batch-hdr { display: flex; align-items: flex-start; gap: 12px; padding: 14px 18px; background: #fffbeb; border: 1.5px solid #fde68a; border-radius: 9px; margin-bottom: 20px; }
  .jep-batch-title { font-weight: 700; font-size: 14px; color: #92400e; margin-bottom: 3px; }
  .jep-batch-sub   { font-size: 12px; color: #b45309; }
  .jep-server-badge { display: inline-flex; align-items: center; gap: 5px; font-size: 10.5px; padding: 3px 9px; border-radius: 99px; background: #f0fdf4; color: #065f46; border: 1px solid #86efac; font-weight: 600; margin-left: 8px; }
  .jep-active-batch { background: #fff; border: 1px solid #c7d2fe; border-radius: 10px; overflow: hidden; margin-bottom: 20px; }
  .jep-active-batch-hdr { display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: #eef2ff; border-bottom: 1px solid #c7d2fe; }
  .jep-active-batch-title { font-weight: 600; font-size: 13px; color: #3730a3; flex: 1; }
  .jep-active-batch-id { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: #6366f1; }
  .jep-entity-queue { display: flex; flex-direction: column; }
  .jep-entity-row { display: flex; align-items: center; gap: 12px; padding: 10px 16px; border-bottom: 1px solid #f3f4f6; }
  .jep-entity-row:last-child { border-bottom: none; }
  .jep-entity-row.running { background: #f5f3ff; }
  .jep-entity-row.done    { background: #f0fdf4; }
  .jep-entity-row.failed  { background: #fef2f2; }
  .jep-entity-num { width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; background: #f3f4f6; color: #6b7280; }
  .jep-entity-row.running .jep-entity-num { background: #ede9fe; color: #5b21b6; }
  .jep-entity-row.done    .jep-entity-num { background: #dcfce7; color: #15803d; }
  .jep-entity-row.failed  .jep-entity-num { background: #fee2e2; color: #b91c1c; }
  .jep-entity-name { font-weight: 600; font-size: 13px; color: #111827; }
  .jep-entity-pid  { font-family: 'JetBrains Mono', monospace; font-size: 9.5px; color: #9ca3af; }
  .jep-entity-time { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: #9ca3af; flex-shrink: 0; }
  .jep-entity-err  { font-size: 11px; color: #b91c1c; }
  .jep-status-pill { font-size: 10px; padding: 2px 8px; border-radius: 99px; font-weight: 600; border: 1px solid; white-space: nowrap; flex-shrink: 0; }
  .jep-status-pill.queued  { background: #f3f4f6; color: #6b7280;  border-color: #d1d5db; }
  .jep-status-pill.running { background: #ede9fe; color: #5b21b6;  border-color: #c4b5fd; }
  .jep-status-pill.done    { background: #dcfce7; color: #15803d;  border-color: #86efac; }
  .jep-status-pill.failed  { background: #fee2e2; color: #b91c1c;  border-color: #fca5a5; }
  .jep-batch-footer { display: flex; align-items: center; gap: 10px; padding: 10px 16px; border-top: 1px solid #e5e7eb; background: #f9fafb; font-size: 12px; color: #6b7280; }
  .jep-poll-note { font-size: 10px; color: #6366f1; font-family: 'JetBrains Mono', monospace; margin-left: auto; }
  .jep-btn-sm { all: unset; box-sizing: border-box; padding: 4px 10px; border-radius: 5px; cursor: pointer; font-size: 11.5px; font-weight: 500; border: 1px solid #c7d2fe; background: #eef2ff; color: #4338ca; }
  .jep-btn-sm:hover { background: #e0e7ff; }
  .jep-resume-banner { padding: 16px 18px; border-radius: 10px; margin-bottom: 18px; background: #f0fdfa; border: 1.5px solid #5eead4; }
  .jep-resume-prog-track { height: 6px; border-radius: 99px; background: #ccfbf1; overflow: hidden; margin: 8px 0 4px; }
  .jep-resume-prog-fill  { height: 100%; border-radius: 99px; background: #0f766e; transition: width .5s ease; }
  .jep-mode-row { display: flex; gap: 10px; margin-bottom: 22px; }
  .jep-mode-btn { all: unset; box-sizing: border-box; flex: 1; padding: 11px 16px; border-radius: 9px; border: 1.5px solid #e5e7eb; background: #fff; cursor: pointer; transition: all .14s; text-align: left; }
  .jep-mode-btn:hover { border-color: #a5b4fc; }
  .jep-mode-btn.active-resume { border-color: #0f766e; background: #f0fdfa; }
  .jep-mode-btn.active-fresh  { border-color: #6366f1; background: #eef2ff; }
  .jep-mode-label { font-size: 13px; font-weight: 600; color: #111827; display: flex; align-items: center; gap: 6px; margin-bottom: 3px; }
  .jep-mode-desc  { font-size: 11.5px; color: #6b7280; line-height: 1.5; }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface TableDescriptor {
  origName:   string;
  label:      string;
  groupCount: number;
  id:         string;   // stable key for React
  unit?:      "section" | "column";
}

function getTables(entity: any): TableDescriptor[] {
  const allGroups: any[] = (entity?.field ?? []).filter(
    (f: any) => f.eClass === "FieldGroup" && f.origName
  );

  if (allGroups.length === 0) {
    // Fallback: entity itself is the source
    return entity?.origName
      ? [{ origName: entity.origName, label: entity.origName, groupCount: 1, id: entity.id ?? entity.origName, unit: "section" }]
      : [];
  }

  // Deduplicate by origName — preserving first-seen order
  const seen  = new Map<string, TableDescriptor>();
  for (const g of allGroups) {
    const orig: string = g.origName;
    if (!seen.has(orig)) {
      seen.set(orig, { origName: orig, label: orig, groupCount: 0, id: orig, unit: "section" });
    }
    seen.get(orig)!.groupCount++;
  }
  return Array.from(seen.values());
}

function fmtTime(iso?: string | null): string {
  if (!iso) return "";
  try {
    let s = iso.trim();
    // Backend sometimes sends UTC without suffix — normalize
    if (!s.endsWith("Z") && !s.includes("+") && !s.includes("-", 10)) {
      s += "Z";
    }
    return parseServerTime(s).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function findIncompleteRun(jobs: any[]): IncompleteRun | null {
  if (!jobs.length) return null;

  // ── Group by process_id ────────────────────────────────────────────────
  const byProcess = new Map<string, any[]>();
  for (const j of jobs) {
    if (!j.process_id) continue;
    if (!byProcess.has(j.process_id)) byProcess.set(j.process_id, []);
    byProcess.get(j.process_id)!.push(j);
  }

  const TERMINAL_OK  = new Set(["success", "completed", "done"]);
  const TERMINAL_BAD = new Set(["failed", "fail", "error", "cancelled"]);
  const NOT_STARTED  = new Set(["yet_to_start", "pending", "queued"]);

  // ── Only consider the MOST RECENT process ──────────────────────────────
  // Sort processes by latest job start_time descending
  const processEntries = Array.from(byProcess.entries())
    .map(([pid, pJobs]) => ({
      pid,
      pJobs,
      latestStart: Math.max(...pJobs.map(j => parseServerTime(j.start_time || 0).getTime())),
    }))
    .sort((a, b) => b.latestStart - a.latestStart);

  if (!processEntries.length) return null;

  // Only look at the most recent process
  const { pid, pJobs, latestStart } = processEntries[0];

  // ── Staleness check: ignore processes older than 7 days ────────────────
  const STALE_MS = 7 * 24 * 60 * 60 * 1000;
  if (Date.now() - latestStart > STALE_MS) return null;

  const succJobs    = pJobs.filter(j => TERMINAL_OK.has((j.status || "").toLowerCase()));
  const failedJobs  = pJobs.filter(j => TERMINAL_BAD.has((j.status || "").toLowerCase()));
  const runningJobs = pJobs.filter(j => ["running", "started", "in_progress"].includes((j.status || "").toLowerCase()));
  const notStarted  = pJobs.filter(j => NOT_STARTED.has((j.status || "").toLowerCase()));

  // Nothing to resume: all succeeded or no progress at all
  if (failedJobs.length === 0 && runningJobs.length === 0 && notStarted.length === 0) return null;
  if (succJobs.length === 0 && runningJobs.length === 0) return null;  // all failed, nothing done — fresh start is better

  const sortedByTime = [...pJobs].sort((a, b) =>
    parseServerTime(b.start_time || 0).getTime() - parseServerTime(a.start_time || 0).getTime()
  );
  const latest   = sortedByTime[0];
  const progress = Math.max(...pJobs.map(j => j.progress || 0));
  const activeJob = runningJobs[0] || failedJobs[0];
  const message   = activeJob?.message || latest?.message || null;

  const status: IncompleteRun["status"] =
    failedJobs.length > 0                                  ? "failed" :
    (progress >= 99 && latest?.end_time)                   ? "stuck" :
    (runningJobs.length === 0 && notStarted.length > 0)    ? "failed" :
    "running";

  return {
    process_id:    pid,
    progress,
    message,
    start_time:    latest?.start_time || null,
    status,
    done_types:    succJobs.map(j => j.job_type).filter(Boolean),
    pending_types: [...failedJobs, ...runningJobs, ...notStarted].map(j => j.job_type).filter(Boolean),
  };
}

// ─── ActiveBatchPanel (unchanged) ─────────────────────────────────────────────
function ActiveBatchPanel({ batchId, batchStatus, entities, onDismiss }: {
  batchId: string; batchStatus: string; entities: BatchEntityProgress[]; onDismiss: () => void;
}) {
  const done    = entities.filter(e => e.status === "done").length;
  const failed  = entities.filter(e => e.status === "failed").length;
  const running = entities.filter(e => e.status === "running").length;
  const queued  = entities.filter(e => e.status === "queued").length;
  const isDone  = batchStatus === "done" || batchStatus === "failed";

  return (
    <div className="jep-active-batch">
      <div className="jep-active-batch-hdr">
        {!isDone && <span className="jep-ring-sm" style={{ flexShrink: 0 }} />}
        <div className="jep-active-batch-title">
          {isDone ? (batchStatus === "done" ? "✅ Batch complete" : "⚠️ Batch finished with errors") : "Batch running on server…"}
        </div>
        <span className="jep-active-batch-id">{batchId.substring(0, 8)}…</span>
        {isDone && <button className="jep-btn-sm" onClick={onDismiss}>Dismiss</button>}
      </div>
      <div className="jep-entity-queue">
        {entities.map((e, i) => (
          <div key={e.entity_id} className={`jep-entity-row ${e.status}`}>
            <div className="jep-entity-num">{i + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="jep-entity-name">{e.entity_label || e.entity_id}</div>
              {e.process_id && <div className="jep-entity-pid">pid: {e.process_id.substring(0, 8)}…</div>}
              {e.error      && <div className="jep-entity-err">{e.error}</div>}
            </div>
            <div className="jep-entity-time">
              {e.started_at  && fmtTime(e.started_at)}
              {e.finished_at && ` → ${fmtTime(e.finished_at)}`}
            </div>
            <div className={`jep-status-pill ${e.status}`}>
              {e.status === "queued"  && "⏳ queued"}
              {e.status === "running" && <><span className="jep-ring-sm" style={{ marginRight: 4 }} />running</>}
              {e.status === "done"    && "✓ done"}
              {e.status === "failed"  && "✕ failed"}
            </div>
          </div>
        ))}
      </div>
      <div className="jep-batch-footer">
        <span>{done} done · {failed} failed · {running} running · {queued} queued</span>
        {!isDone && <span className="jep-poll-note">polling every 5s · safe to leave this page</span>}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const JobExecutionPage: React.FC<Props> = ({
  selectedEntity, selectedEntities, appNm, isBatchMode, entityJobs, tool,
}) => {
  const [selected,   setSelected]   = useState<Set<JobType>>(new Set());
  const [fileMap,    setFileMap]    = useState<Record<string, File | null>>({});
  // E1: which reviewed rules run (approved by default) and what they need from the files
  const [ruleSet,    setRuleSet]    = useState<"approved" | "approved+preapproved">("approved");
  const [plan,       setPlan]       = useState<any>(null);
  // source of the data: uploaded files, or a connected database (push-down: the rules run inside it)
  const [srcKind,    setSrcKind]    = useState<"files" | "db">("files");
  const [conns,      setConns]      = useState<any[]>([]);
  const [connId,     setConnId]     = useState<string>("");
  const [schemaName, setSchemaName] = useState<string>("public");
  useEffect(() => {
    if (srcKind !== "db") return;
    adriva_listConnections().then((cs: any[]) => { setConns(cs || []); if (!connId && cs?.length) setConnId(String(cs[0].id)); }).catch(() => setConns([]));
  }, [srcKind]); // eslint-disable-line
  const [runAll,     setRunAll]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result,     setResult]     = useState<{ ok: boolean; msg: string } | null>(null);
  const [runMode,    setRunMode]    = useState<RunMode>("fresh");
  const [incomplete, setIncomplete] = useState<IncompleteRun | null>(null);
  const [activeBatchId,       setActiveBatchId]       = useState<string | null>(null);
  const [activeBatchStatus,   setActiveBatchStatus]   = useState<string>("queued");
  const [activeBatchEntities, setActiveBatchEntities] = useState<BatchEntityProgress[]>([]);
  // Source files fetched on demand when the DM tree didn't ship the entity's fields (lazy mode)
  const [fetchedTables, setFetchedTables] = useState<TableDescriptor[] | null>(null);
  const [tablesLoading, setTablesLoading] = useState(false);

  // ── Active pipeline detection ─────────────────────────────────────────────
  const [activePipeline, setActivePipeline] = useState<{
    active: boolean;
    process_id?: string;
    job_types?: string[];
    started_at?: string;
  } | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);


  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const effectiveEntity = isBatchMode ? (selectedEntities[0] ?? null) : selectedEntity;

  useEffect(() => {
    setSelected(new Set()); setFileMap({}); setRunAll(false); setResult(null);
    setActiveBatchId(null); setActiveBatchStatus("queued"); setActiveBatchEntities([]);
    if (!isBatchMode && entityJobs?.length) {
      const inc = findIncompleteRun(entityJobs);
      setIncomplete(inc);
      setRunMode(inc ? "resume" : "fresh");
    } else {
      setIncomplete(null);
      setRunMode("fresh");
    }
  }, [selectedEntity?.id, isBatchMode, entityJobs]);

  // ── Fetch source files when the entity's field groups aren't loaded locally ──
  useEffect(() => {
    const ent = effectiveEntity;
    setFetchedTables(null);
    if (!ent?.entityId) return;
    if (getTables(ent).length > 0) return;   // DM already provided field groups — no fetch needed
    let cancelled = false;
    setTablesLoading(true);
    getEntitySourceFiles(appNm, ent.entityId)
      .then(res => {
        if (cancelled) return;
        const tabs = (res?.success ? res.tables : []) || [];
        setFetchedTables(tabs.map((t: any) => ({
          origName:   t.origName,
          label:      t.label || t.origName,
          groupCount: t.columnCount ?? 0,
          id:         t.id || t.origName,
          unit:       "column",
        })));
      })
      .catch(() => { if (!cancelled) setFetchedTables([]); })
      .finally(() => { if (!cancelled) setTablesLoading(false); });
    return () => { cancelled = true; };
  }, [effectiveEntity?.entityId, appNm]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── Poll for active pipeline on this entity ───────────────────────────────
  const activePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkActivePipeline = useCallback(async () => {
    if (!effectiveEntity?.entityId) return;
    try {
      const res = await adriva_getActivePipeline(effectiveEntity.entityId);
      setActivePipeline(res);
    } catch {
      setActivePipeline(null);
    }
  }, [effectiveEntity?.entityId]);

  useEffect(() => {
    checkActivePipeline();
    activePollRef.current = setInterval(checkActivePipeline, 10000);
    return () => { if (activePollRef.current) clearInterval(activePollRef.current); };
  }, [checkActivePipeline]);


  const startPolling = useCallback((batchId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const poll = async () => {
      try {
        const res = await adriva_getBatchStatus(batchId);
        if (res.success) {
          setActiveBatchStatus(res.status);
          setActiveBatchEntities(res.entities as BatchEntityProgress[]);
          if (res.status === "done" || res.status === "failed") clearInterval(pollRef.current!);
        }
      } catch { /* keep polling */ }
    };
    poll();
    pollRef.current = setInterval(poll, 5000);
  }, []);

  const handleCancel = useCallback(async () => {
    if (!effectiveEntity?.entityId) return;
    setCancelling(true);
    try {
      await adriva_cancelPipeline(effectiveEntity.entityId);
      setActivePipeline({ active: false });
      setResult({ ok: true, msg: "Pipeline cancelled. You can now start a new run." });
    } catch (e: any) {
      setResult({ ok: false, msg: `Cancel failed: ${e.message}` });
    } finally {
      setCancelling(false);
      setShowConflictModal(false);
    }
  }, [effectiveEntity?.entityId]);

  // tables: prefer the DM's own field groups; fall back to the fetched source files (lazy mode)
  const localTables   = getTables(effectiveEntity);
  const tables: TableDescriptor[] = localTables.length ? localTables : (fetchedTables ?? []);
  const toggleJob     = (jt: JobType) => setSelected(prev => { const n = new Set(prev); n.has(jt) ? n.delete(jt) : n.add(jt); return n; });
  const activeJobTypes: JobType[] = runAll
    ? JOB_CATALOG.filter(j => !RETIRED_JOBS.has(j.job_type)).map(j => j.job_type)
    : JOB_CATALOG.filter(j => selected.has(j.job_type)).map(j => j.job_type) as JobType[];
  const orderedActive = JOB_CATALOG.filter(j => activeJobTypes.includes(j.job_type));
  const needsFiles    = orderedActive.some(j => j.requiresFiles);
  // DQ Rule Execution on its own (fresh, single entity) -> the dedicated execution panel replaces the file section + Run
  const dqOnly = runMode === "fresh" && !isBatchMode && activeJobTypes.length === 1 && activeJobTypes[0] === "DQ_RULE_EXECUTION";
  // E1: the execution plan - fetched after needsFiles is known (no use before definition)
  useEffect(() => {
    if (!needsFiles || !selectedEntity?.entityId) { setPlan(null); return; }
    let live = true;
    adriva_getExecutionPlan(selectedEntity.entityId, ruleSet).then((p: any) => { if (live) setPlan(p); }).catch(() => { if (live) setPlan(null); });
    return () => { live = false; };
  }, [needsFiles, selectedEntity?.entityId, ruleSet]); // eslint-disable-line
  // allFilesReady: every unique source file (not every group) must have a file chosen
  const allFilesReady = !needsFiles || (srcKind === "db" ? !!connId
    : (tables.length > 0 && tables.every(t => !!fileMap[t.origName])));

  const canRunFresh  = activeJobTypes.length > 0 && allFilesReady && (isBatchMode ? selectedEntities.length > 0 : !!selectedEntity);
  const canRunResume = !!incomplete && !!selectedEntity && !isBatchMode;
  const canRun       = runMode === "resume" ? canRunResume : canRunFresh;
  // TWO STEPS: 1 = choose the jobs, 2 = their inputs (documents for Enterprise Knowledge, source data for DQ Rule
  // Execution) - shown on a second page with Back, instead of below the job cards
  const ekSelected = activeJobTypes.includes("ENTERPRISE_KNOWLEDGE_DISCOVERY");
  const hasInputs  = runMode === "fresh" && !isBatchMode && (needsFiles || ekSelected || dqOnly);
  const [step, setStep] = useState<1 | 2>(1);
  useEffect(() => { if (!hasInputs) setStep(1); }, [hasInputs]);
  const inputsFor = [ekSelected ? "Build Enterprise Knowledge" : "", (needsFiles || dqOnly) ? "DQ Rule Execution" : ""].filter(Boolean);
  const batchRunning = isBatchMode && !!activeBatchId && activeBatchStatus === "running";

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleResume = async () => {
    if (!canRunResume || !selectedEntity || !incomplete) return;
    setSubmitting(true); setResult(null);
    try {
      const pendingTypes = incomplete.pending_types.length > 0 ? incomplete.pending_types : [];
      const res = await adriva_runPipeline(appNm, selectedEntity.entityId, incomplete.process_id, pendingTypes, true);
      setResult(res.success
        ? { ok: true,  msg: `Resuming · skipping ${incomplete.done_types.length} done job(s) · running ${pendingTypes.length || "remaining"} · Process: ${incomplete.process_id.substring(0, 8)}…` }
        : { ok: false, msg: res.message || "Resume failed." }
      );
    } catch (e: any) {
      setResult({ ok: false, msg: `Error: ${e.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSingleRun = async () => {
    if (!canRunFresh || !selectedEntity) return;

    // ★ Check for active pipeline first
    if (activePipeline?.active) {
      setPendingAction(() => doSingleRun);
      setShowConflictModal(true);
      return;
    }
    doSingleRun();
  };

  const doSingleRun = async () => {
    setShowConflictModal(false);
    setSubmitting(true); setResult(null);
    try {
      const hasNonDQ = activeJobTypes.some(jt => jt !== "DQ_RULE_EXECUTION");
      if (!hasNonDQ) {
        const meta = {
          app_nm: appNm, entity_id: selectedEntity!.entityId, dm_id: selectedEntity!.id,
          job_types: activeJobTypes,
          input: { dm_id: selectedEntity!.id, run_all: true, lifecycle_filters: ["Approved"],
                   rule_set: ruleSet },        // engine chosen per application by the server (reviewed rules -> v2, else legacy)
          source_file_map: tables.reduce((acc: any, t) => {
            if (fileMap[t.origName]) acc[t.origName] = fileMap[t.origName]!.name; return acc;
          }, {}),
        };
        const res = srcKind === "db"
          ? await adriva_executeOnConnection({ app_nm: appNm, entity_id: selectedEntity!.entityId, connection_id: connId,
              schema_name: schemaName || "public", rule_set: ruleSet, dm_id: selectedEntity!.id })
          : await adriva_executeDQWithUpload(meta, fileMap as Record<string, File>);
        setResult(res.success ? { ok: true, msg: `Job ID: ${res.job_id ?? "—"} — ${res.message || ""}` } : { ok: false, msg: res.message || "Failed." });
        if (res.success && srcKind === "files") setFileMap({});
      } else {
        const jobsToRun = runAll ? [] : activeJobTypes.filter(jt => jt !== "DQ_RULE_EXECUTION");
        const res = await adriva_runPipeline(appNm, selectedEntity!.entityId, "", jobsToRun);
        if (res.status === "conflict") {
          // Server confirmed conflict — show modal
          setActivePipeline({ active: true, process_id: res.active_process_id, job_types: res.active_job_types, started_at: res.active_started_at });
          setPendingAction(() => doSingleRun);
          setShowConflictModal(true);
          return;
        }
        setResult(res.success
          ? { ok: true,  msg: `Pipeline started · Process: ${res.process_id}` }
          : { ok: false, msg: res.message || "Failed." });
      }
      checkActivePipeline();
    } catch (e: any) {
      setResult({ ok: false, msg: `Error: ${e.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBatchRun = async () => {
    if (!canRun || selectedEntities.length === 0) return;
    setSubmitting(true); setResult(null);
    try {
      const res = await adriva_runBatch({
        app_nm:   appNm,
        entities: selectedEntities.map(e => ({ entity_id: e.entityId ?? e.id, entity_label: e.label ?? "" })),
        jobs:     runAll ? undefined : activeJobTypes.length > 0 ? [...activeJobTypes] : undefined,
      });
      if (res.success && res.batch_id) {
        setActiveBatchId(res.batch_id);
        setActiveBatchStatus("queued");
        setActiveBatchEntities(selectedEntities.map(e => ({ entity_id: e.entityId ?? e.id, entity_label: e.label ?? "", status: "queued" as const })));
        startPolling(res.batch_id);
      } else {
        setResult({ ok: false, msg: res.message || "Failed to start batch." });
      }
    } catch (e: any) {
      setResult({ ok: false, msg: `Error: ${e.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRun = isBatchMode ? handleBatchRun : (runMode === "resume" ? handleResume : handleSingleRun);

  // ── Guards ────────────────────────────────────────────────────────────────
  if (!isBatchMode && !selectedEntity) return (
    <div className="jep"><style>{CSS}</style>
      <div className="jep-empty">
        <span style={{ fontSize: 48 }}>🗂</span>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#374151" }}>No entity selected</div>
        <div style={{ fontSize: 13.5 }}>Choose an entity from the top bar.</div>
      </div>
    </div>
  );

  if (isBatchMode && selectedEntities.length === 0) return (
    <div className="jep"><style>{CSS}</style>
      <div className="jep-empty">
        <span style={{ fontSize: 48 }}>☑️</span>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#374151" }}>No entities selected</div>
        <div style={{ fontSize: 13.5 }}>Click entity tabs at the top to select them.</div>
      </div>
    </div>
  );

  // ── Job card renderer ─────────────────────────────────────────────────────
  const renderGroup = (jobs: readonly typeof JOB_CATALOG[number][], label: string) => (
    <>
      <div className="jep-group-title">{label}</div>
      <div className="jep-grid" style={{ marginBottom: 16 }}>
        {jobs.map(job => {
          const on     = selected.has(job.job_type);
          const isDone = runMode === "resume" && !!incomplete?.done_types.includes(job.job_type);
          const isPend = runMode === "resume" && !!incomplete?.pending_types.includes(job.job_type);
          return (
            <div key={job.job_type}
              className={`jep-card${on && !isDone ? " sel" : ""}${isDone ? " done-card" : ""}${isPend && !isDone ? " pending-card" : ""}`}
              onClick={() => { if (!isDone) toggleJob(job.job_type); }}
              title={isDone ? "Already completed — will be skipped on resume" : isPend ? "Will be retried on resume" : undefined}
            >
              {on && !isDone     && <div className="jep-check">✓</div>}
              {isDone            && <div className="jep-done-badge">✓ done</div>}
              {isPend && !isDone && <div className="jep-pending-badge">↻ retry</div>}
              <div className="jep-card-head">
                <span style={{ fontSize: 18 }}>{job.icon}</span>
                <span className="jep-card-label">{job.label}</span>
              </div>
              <div className="jep-card-desc">{job.desc}</div>
              {job.requiresFiles && <span className="jep-file-badge">📁 Requires source files</span>}
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <div className="jep">
      <style>{CSS}</style>

      {isBatchMode && (
        <div className="jep-batch-hdr">
          <span style={{ fontSize: 22 }}>🗂</span>
          <div style={{ flex: 1 }}>
            <div className="jep-batch-title">
              Batch Execution
              <span className="jep-server-badge">⚡ server-side queue</span>
            </div>
            <div className="jep-batch-sub">
              {selectedEntities.length} {selectedEntities.length === 1 ? "entity" : "entities"} ·
              Runs sequentially on server — safe to leave this page after submitting.
            </div>
            <div style={{ marginTop: 6, fontSize: 11.5, color: "#b45309" }}>
              Order: {selectedEntities.map(e => e.label).join(" → ")}
            </div>
          </div>
        </div>
      )}

      {/* ═══ Active pipeline banner ══════════════════════════════════════════ */}
      {!isBatchMode && activePipeline?.active && (
        <div style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "14px 18px", marginBottom: 18, borderRadius: 10,
          background: "#fef3c7", border: "1.5px solid #fde68a",
        }}>
          <span className="jep-ring-sm" style={{
            borderColor: "rgba(180,83,9,0.2)",
            borderTopColor: "#b45309",
            flexShrink: 0,
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
              A pipeline is currently running on this entity
            </div>
            <div style={{ fontSize: 11.5, color: "#b45309", marginTop: 3 }}>
              Process: <code style={{
                background: "#fffbeb", padding: "1px 5px", borderRadius: 3,
                fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              }}>
                {activePipeline.process_id?.substring(0, 12)}…
              </code>
              {activePipeline.started_at && (
                <> · Started {fmtTime(activePipeline.started_at)}</>
              )}
              {activePipeline.job_types?.length ? (
                <> · Jobs: {activePipeline.job_types
                  .map(t => t.replace(/DG_|DQ_/g, "").replace(/_/g, " ").toLowerCase())
                  .join(", ")
                }</>
              ) : null}
            </div>
          </div>
          <button
            onClick={handleCancel}
            disabled={cancelling}
            style={{
              all: "unset", boxSizing: "border-box",
              padding: "7px 16px", borderRadius: 7,
              background: cancelling ? "#fde68a" : "#fff",
              border: "1px solid #f59e0b",
              color: "#92400e", fontSize: 12, fontWeight: 600,
              cursor: cancelling ? "default" : "pointer",
              display: "flex", alignItems: "center", gap: 6,
              transition: "all 0.15s", whiteSpace: "nowrap" as const,
            }}
          >
            {cancelling
              ? <><span className="jep-ring-sm" style={{ width: 11, height: 11 }} /> Cancelling…</>
              : <>⏹ Stop Pipeline</>
            }
          </button>
        </div>
      )}

      {/* ═══ Conflict modal ══════════════════════════════════════════════════ */}
      {showConflictModal && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.35)", display: "flex",
          alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "#fff", borderRadius: 14, padding: "28px 32px",
            maxWidth: 480, width: "90%",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
              Pipeline Already Running
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7, marginBottom: 20 }}>
              A pipeline is already active for this entity
              {activePipeline?.started_at && (
                <> (started {fmtTime(activePipeline.started_at)})</>
              )}.
              Starting a new run while the old one is still in progress can
              cause data corruption. What would you like to do?
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
              <button
                onClick={async () => {
                  await handleCancel();
                  // After cancel, run the pending action
                  setTimeout(() => { if (pendingAction) pendingAction(); }, 500);
                }}
                disabled={cancelling}
                style={{
                  all: "unset", boxSizing: "border-box",
                  padding: "12px 18px", borderRadius: 9,
                  background: "#fef2f2", border: "1.5px solid #fca5a5",
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: "#b91c1c" }}>
                  ⏹ Stop current pipeline & start new
                </div>
                <div style={{ fontSize: 11.5, color: "#dc2626", marginTop: 3 }}>
                  Cancels the running pipeline, marks its jobs as cancelled, then starts your new run.
                </div>
              </button>

              <button
                onClick={() => setShowConflictModal(false)}
                style={{
                  all: "unset", boxSizing: "border-box",
                  padding: "12px 18px", borderRadius: 9,
                  background: "#f9fafb", border: "1.5px solid #e5e7eb",
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                  ← Wait for current pipeline to finish
                </div>
                <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 3 }}>
                  Go back and let the running pipeline complete. You can monitor it in the Mon tab.
                </div>
              </button>
            </div>

            <div style={{ textAlign: "right" }}>
              <button
                onClick={() => setShowConflictModal(false)}
                style={{
                  all: "unset", fontSize: 12, color: "#9ca3af",
                  cursor: "pointer", padding: "4px 8px",
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {activeBatchId && (
        <ActiveBatchPanel
          batchId={activeBatchId} batchStatus={activeBatchStatus}
          entities={activeBatchEntities}
          onDismiss={() => { setActiveBatchId(null); if (pollRef.current) clearInterval(pollRef.current); }}
        />
      )}

      {!isBatchMode && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, fontWeight: 500, letterSpacing: 1.5, textTransform: "uppercase", color: "#9ca3af", marginBottom: 4 }}>Pipeline Trigger</div>
          <h2 style={{ fontSize: 19, fontWeight: 700, color: "#111827" }}>{selectedEntity.label}</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 5, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "#6b7280" }}>System: <strong style={{ color: "#374151" }}>{selectedEntity.dataSrcSystemId || "—"}</strong></span>
            <span style={{ color: "#d1d5db" }}>·</span>
            <span style={{ fontSize: 12, color: "#6b7280" }}>Entity ID: <strong style={{ color: "#374151", fontFamily: "'JetBrains Mono',monospace" }}>{selectedEntity.entityId || "—"}</strong></span>
          </div>
        </div>
      )}

      {!isBatchMode && entityJobs === null && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 18, fontSize: 12, color: "#6b7280" }}>
          <span className="jep-ring-sm" />
          Checking for previous runs…
        </div>
      )}

      {!isBatchMode && incomplete && (
        <>
          <div style={{ padding: "14px 18px", borderRadius: 10, marginBottom: 14, background: "#fffbeb", border: "1.5px solid #fde68a" }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "#92400e", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <span>{incomplete.status === "failed" ? "❌" : incomplete.status === "stuck" ? "⚠️" : "⏸"}</span>
              A previous run was found — how do you want to proceed?
            </div>
            <div className="jep-mode-row" style={{ marginBottom: 0 }}>
              <button className={`jep-mode-btn${runMode === "resume" ? " active-resume" : ""}`} onClick={() => setRunMode("resume")}>
                <div className="jep-mode-label"><span>⏭</span> Resume from checkpoint</div>
                <div className="jep-mode-desc">
                  Skip {incomplete.done_types.length} completed job{incomplete.done_types.length !== 1 ? "s" : ""} · pick up from where it stopped
                </div>
              </button>
              <button className={`jep-mode-btn${runMode === "fresh" ? " active-fresh" : ""}`} onClick={() => setRunMode("fresh")}>
                <div className="jep-mode-label"><span>🔄</span> Start fresh</div>
                <div className="jep-mode-desc">Ignore previous run · select and run jobs from scratch</div>
              </button>
            </div>
          </div>

          <div className="jep-resume-banner" style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: "#0d9488" }}>
              {incomplete.done_types.length > 0 && (
                <span>✓ <strong>{incomplete.done_types.length}</strong> completed: {incomplete.done_types.map(t => t.replace(/DG_|DQ_/g, "").replace(/_/g, " ").toLowerCase()).join(", ")}</span>
              )}
              {incomplete.pending_types.length > 0 && (
                <span style={{ color: "#b45309" }}>↻ <strong>{incomplete.pending_types.length}</strong> remaining: {incomplete.pending_types.map(t => t.replace(/DG_|DQ_/g, "").replace(/_/g, " ").toLowerCase()).join(", ")}</span>
              )}
            </div>
            {incomplete.message && <div style={{ fontSize: 11, color: "#0f766e", fontStyle: "italic", marginTop: 5 }}>Last message: {incomplete.message}</div>}
            <div className="jep-resume-prog-track"><div className="jep-resume-prog-fill" style={{ width: `${Math.min(incomplete.progress, 100)}%` }} /></div>
            <div style={{ fontSize: 10.5, color: "#0d9488", fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>
              {Math.round(incomplete.progress)}% complete · started {fmtTime(incomplete.start_time)}
              <span style={{ marginLeft: 8, opacity: .7 }}>pid: {incomplete.process_id.substring(0, 8)}…</span>
            </div>
          </div>
        </>
      )}

      {(step === 1 || !hasInputs) && (<div className="jep-step back">
      {runMode === "resume" && incomplete ? (
        <div style={{ padding: "10px 14px", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 8, marginBottom: 20, fontSize: 12.5, color: "#0f766e" }}>
          The pipeline will automatically skip completed jobs and restart from the first incomplete step.
          No job selection required — or switch to <strong>Start fresh</strong> to pick specific jobs.
        </div>
      ) : (
        <>
          
          {!runAll && (
            <>
              <div className="jep-section">Select jobs to run</div>
              {stagesFor(tool).main.map(([types, label]) => <React.Fragment key={label}>{renderGroup(pickJobs(types), label)}</React.Fragment>)}
              {stagesFor(tool).more.map(([types, label, hint]) => (
                <details key={label} className="jep-more">
                  <summary>{label} <span>{hint}</span></summary>
                  {renderGroup(pickJobs(types), label)}
                </details>))}
              
            </>
          )}

          {orderedActive.length > 0 && (
            <>
              <div className="jep-divider" />
              <div className="jep-section">Run order</div>
              <div className="jep-pipeline">
                {orderedActive.map((j, i) => {
                  const isSkipped = runMode === "resume" && incomplete?.done_types.includes(j.job_type);
                  const isNext    = runMode === "resume" && !isSkipped && i === orderedActive.findIndex(x => !incomplete?.done_types.includes(x.job_type));
                  return (
                    <React.Fragment key={j.job_type}>
                      {i > 0 && <span className="jep-pip-arrow">→</span>}
                      <span className={`jep-pip-step${j.requiresFiles ? " files" : ""}${isSkipped ? " skipped" : ""}${isNext ? " resume" : ""}`}>
                        {isSkipped ? "✓" : j.icon} {j.label}
                      </span>
                    </React.Fragment>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
      </div>)}

      {hasInputs && step === 2 && (<div className="jep-step fwd">
      <div className="jep-step-head">
        <button className="jep-back" onClick={() => setStep(1)}>← Back to the jobs</button>
        <div>
          <div className="jep-step-title">Step 2 of 2 · inputs</div>
          <div className="jep-step-sub">for {inputsFor.join(" and ")}</div>
        </div>
      </div>

      {ekSelected && selectedEntity && (
        <div className="jep-input-card">
          <div className="jep-section">Documents for Enterprise Knowledge <span className="jep-opt">optional</span></div>
          <div className="jep-step-sub" style={{ marginBottom: 6 }}>Catalog exports (Collibra / Alation Excel, CSV) and business documents (PDF, DOCX, TXT). Every row / passage is tied to the fields it is about and given to the agent for those fields; its proposals cite them and the SME reviews them. Files uploaded earlier stay until removed.</div>
          <KnowledgeSourcesPanel entityId={selectedEntity.entityId} />
        </div>
      )}

      {dqOnly && selectedEntity && (
        <DQExecutionPanel appNm={appNm} entityId={selectedEntity.entityId} dmId={selectedEntity.id}
          tables={tables} tablesLoading={tablesLoading} />
      )}

      {/* ── File upload section ── */}
      {runMode === "fresh" && needsFiles && !dqOnly && (
        <>
          <div className="jep-divider" />
          <div className="jep-section">
            Source file{tables.length === 1 ? "" : "s"} required for DQ Rule Execution
            <span style={{ marginLeft: 8, fontWeight: 400, textTransform: "none", fontSize: 10.5, color: "#9ca3af", letterSpacing: 0 }}>
              ({tablesLoading ? "loading…" : `${tables.length} ${tables.length === 1 ? "file" : "files"}`})
            </span>
          </div>
          {/* E1: which rules run, and what they need from the files */}
          <div className="jep-plan">
            <div className="jep-plan-sel">
              <span>Data from:</span>
              <label><input type="radio" checked={srcKind === "files"} onChange={() => setSrcKind("files")} /> Upload files</label>
              <label><input type="radio" checked={srcKind === "db"} onChange={() => setSrcKind("db")} /> Connected database (rules run inside it)</label>
            </div>
            {srcKind === "db" && <div className="jep-plan-db">
              <label>Connection <select value={connId} onChange={(e) => setConnId(e.target.value)}>
                {conns.length === 0 && <option value="">no saved connection — add one in Data Connections</option>}
                {conns.map((c: any) => <option key={c.id} value={String(c.id)}>{c.name} · {c.connector_type}</option>)}</select></label>
              <label>Schema <input value={schemaName} onChange={(e) => setSchemaName(e.target.value)} /></label>
              <span className="jep-plan-hint">Each table is read as {schemaName || "public"}.&lt;TABLE&gt; (same name). Only counts and a capped list of failing records leave the database.</span>
            </div>}
            <div className="jep-plan-sel">
              <span>Rules to execute:</span>
              <label><input type="radio" checked={ruleSet === "approved"} onChange={() => setRuleSet("approved")} /> Approved only</label>
              <label><input type="radio" checked={ruleSet === "approved+preapproved"} onChange={() => setRuleSet("approved+preapproved")} /> Approved + pre-approved</label>
            </div>
            {plan && (plan.rules === 0
              ? <div className="jep-plan-warn">No {ruleSet === "approved" ? "approved" : "approved or pre-approved"} rule yet — approve rules on the DQ Rules page first{ruleSet === "approved" ? ", or include the pre-approved ones" : ""}.</div>
              : <div className="jep-plan-need"><b>{plan.rules} rule(s)</b> will run. Each file must contain at least these columns
                  (a rule whose table or column is missing is skipped and reported):
                  <ul>{(plan.tables || []).map((t: any) => <li key={t.table}><b>{t.table}</b> · {t.rules} rule(s) · {t.columns.join(", ")}</li>)}</ul></div>)}
          </div>
          <div style={{ display: "none" }}>
          </div>

          {/* loading source files (lazy fetch) */}
          {tablesLoading && tables.length === 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 8, fontSize: 12.5, color: "#6b7280" }}>
              <span className="jep-ring-sm" /> Loading source files…
            </div>
          )}

          {/* no source files resolved */}
          {!tablesLoading && tables.length === 0 && (
            <div className="jep-alert err">⚠ Could not determine the source files for this entity. Make sure profiling has run for it.</div>
          )}

          {srcKind === "files" && tables.map(t => {
            const file = fileMap[t.origName];
            const unit = t.unit || "section";
            return (
              <div key={t.id} className={`jep-file-row${file ? " ready" : ""}`}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="jep-file-name">{t.origName}</div>
                  <div className="jep-file-groups">
                    {t.groupCount > 0
                      ? `${t.groupCount} ${unit}${t.groupCount === 1 ? "" : "s"}${unit === "section" ? " (all map to this file)" : " in this file"}`
                      : "source file"}
                  </div>
                  {file && <div className="jep-file-ok">✓ {file.name}</div>}
                </div>
                <input type="file" accept=".csv,.xlsx" className="jep-file-input"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    if (e.target.files?.[0]) setFileMap(p => ({ ...p, [t.origName]: e.target.files![0] }));
                  }}
                />
              </div>
            );
          })}
        </>
      )}
      </div>)}

      <div className="jep-divider" />

      {result && (
        <div className={`jep-alert${result.ok ? " ok" : " err"}`}>
          {result.ok ? "✅ " : "⚠ "}{result.msg}
        </div>
      )}

      <div style={{ display: (dqOnly && step === 2) ? "none" : "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        {hasInputs && step === 1 ? (
          <button className="jep-run" disabled={activeJobTypes.length === 0 || batchRunning} onClick={() => setStep(2)}>
            Next: {inputsFor.length > 1 ? "inputs" : ekSelected ? "documents" : "source data"} <span style={{ fontSize: 15 }}>→</span>
          </button>
        ) : (<>
        {hasInputs && <button className="jep-back" onClick={() => setStep(1)}>← Back</button>}
        <button
          className={`jep-run${runMode === "resume" ? " teal" : ""}`}
          disabled={!canRun || submitting || batchRunning}
          onClick={handleRun}
        >
          {submitting ? (
            <><div className="jep-ring" />Submitting…</>
          ) : (
            <>
              <span style={{ fontSize: 15 }}>{runMode === "resume" ? "⏭" : "▶"}</span>
              {isBatchMode
                ? `Queue ${activeJobTypes.length || "all"} Job${activeJobTypes.length === 1 ? "" : "s"} × ${selectedEntities.length} ${selectedEntities.length === 1 ? "entity" : "entities"}`
                : runMode === "resume"
                  ? `Resume — skip ${incomplete?.done_types.length || 0} done, run ${incomplete?.pending_types.length || "remaining"}`
                  : runAll ? "Run Full Pipeline" : `Run ${activeJobTypes.length || ""} Job${activeJobTypes.length === 1 ? "" : "s"}`
              }
            </>
          )}
        </button>
        </>)}

        {runMode === "resume" && !submitting && (
          <span style={{ fontSize: 11.5, color: "#0f766e" }}>
            Orchestrator will call <code style={{ background: "#f0fdfa", padding: "1px 5px", borderRadius: 3 }}>resume()</code> on process {incomplete?.process_id.substring(0, 8)}…
          </span>
        )}
        {isBatchMode && !submitting && canRun && !activeBatchId && (
          <span style={{ fontSize: 11.5, color: "#6b7280" }}>Submits to server — safe to navigate away</span>
        )}
        {batchRunning && (
          <span style={{ fontSize: 11.5, color: "#6366f1" }}>Batch is running — submit another after it completes</span>
        )}
      </div>
    </div>
  );
};

export default JobExecutionPage;